import { useState } from "react"
import { DateTime } from "luxon"
import { useFormikContext } from "formik"
import { Grid, MenuItem, Stack } from "@mui/material"
import { FormikDropdown, FormikSwitch, FormikTextField } from "../../../../../shared/components/forms/Formik"
import { FormikImageUpload } from "../../../../../shared/components/Formik/FormikImageUpload"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardAlertInfo } from "../../../../../shared/components/alert/StandardAlertInfo"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { FormikDeliveryLocationField } from "./FormikDeliveryLocationField"
import { AvailabilityDetailsField } from "./AvailabilityDetailsField"
import { FormikProductGroupField } from "./FormikProductGroupField"
import { CreateUpdateProductFormValues } from "../productsTypes"
import { FormikAllergensField } from "./FormikAllergensField"
import { DeadlineField, dayOptions } from "./DeadlineField"
import { FormikCurrencyField } from "./FormikCurrencyField"
import { OfferDetails } from "./offerDetails/OfferDetails"
import { FormikShopsField } from "./FormikShopsField"
import { ItemLimitsField } from "./ItemLimitsField"
import { PriceListBox } from "./PriceListBox"
import { SelectProductGroupCode } from "../../productGroup/components/SelectProductGroupCode"

type CreateUpdateProductFormContentProps = Readonly<{}>

export function CreateUpdateProductFormContent({}: CreateUpdateProductFormContentProps) {
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const defaultTime = DateTime.fromISO("12:00")
    const [checkedDeadline, setCheckedDeadline] = useState(!!values?.deadline)
    const [checkedItemLimits, setCheckedItemLimits] = useState(!!values?.itemLimits)
    const [checkedOffer, setCheckedOffer] = useState(!!values?.offerDetails)
    const [offerDialogState, setOfferDialogState] = useState<boolean>(false)

    const handleChangeDeadline = (checked: boolean) => {
        setCheckedDeadline(checked)
        if (checked) {
            setFieldValue("deadline.purchases.daysBefore", dayOptions[0].value)
            setFieldValue("deadline.purchases.timeOfDay", defaultTime)
            setFieldValue("deadline.cancellation.daysBefore", dayOptions[0].value)
            setFieldValue("deadline.cancellation.timeOfDay", defaultTime)
        } else setFieldValue("deadline", null)
    }
    const handleChangeItemLimits = (checked: boolean) => {
        setCheckedItemLimits(checked)
        if (checked) {
            setFieldValue("itemLimits.maxDailyItemsPerPerson", values.itemLimits?.maxDailyItemsPerPerson || "")
            setFieldValue("itemLimits.minItemsPerOrder", values.itemLimits?.minItemsPerOrder || "")
        } else setFieldValue("itemLimits", null)
    }

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    <FormikProductGroupField name="productGroup" />

                    <LocalizedStrict id="product-create-name-field-label" attrs={{ label: true }}>
                        <FormikTextField name="name" label="Product name" fullWidth />
                    </LocalizedStrict>

                    <Grid container item spacing={1} direction="row">
                        <Grid item sm={2.5}>
                            <LocalizedStrict id="product-create-currency-field-label" attrs={{ label: true }}>
                                <FormikDropdown name="currency" label="Currency" fullWidth>
                                    <MenuItem value="DKK">DKK</MenuItem>
                                </FormikDropdown>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item sm={3}>
                            <LocalizedStrict id="product-create-price-field-label" attrs={{ label: true }}>
                                <FormikCurrencyField name="price" label="Price" errorMessage={errors.price} />
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </StandardMainPanel>
                <LocalizedStrict id="product-create-panel-access" attrs={{ title: true }}>
                    <StandardMainPanel title="Access">
                        <Stack gap="2rem">
                            <LocalizedStrict id="product-create-panel-access-shop-alert-title" attrs={{ title: true }}>
                                <StandardAlertInfo title="A shop represents a virtual or physical point of sale, such as webshops in the GoPay app or the shop connected to POS terminals in a canteen. A user that is NOT connected to a price list will see the products that are directly assigned to shops here." />
                            </LocalizedStrict>
                            <FormikShopsField name="shops" />
                            <LocalizedStrict
                                id="product-create-panel-access-price-list-alert-title"
                                attrs={{ title: true }}
                            >
                                <StandardAlertInfo title="Price lists can be used to offer different selections of products or different prices to different groups of users. Several price lists can be associated with the same shop in order to show different selections. A user will always see a single price list - or - the default selection. What price list is shown depends on the user group or - in the case of POS - the subscription type of the user." />
                            </LocalizedStrict>
                            <PriceListBox />
                        </Stack>
                    </StandardMainPanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-panel-description" attrs={{ title: true }}>
                    <StandardMainPanel title="Description">
                        <LocalizedStrict id="product-create-description-field-label" attrs={{ label: true }}>
                            <FormikTextField name="description" label="Description" rows={4} fullWidth multiline />
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-image-field-label" attrs={{ label: true }}>
                            <FormikImageUpload label="Image" name="productPhoto" typeOfEntity="product" />
                        </LocalizedStrict>
                    </StandardMainPanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-general-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="product-create-active-switch">
                            <FormikSwitch name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-payment-price-switch">
                            <FormikSwitch name="paymentDetails.isPriceHidden">Hide price:</FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="product-create-product-description-marketing-tag-field"
                            attrs={{ label: true, helperText: true }}
                        >
                            <FormikTextField
                                name="productDescription.marketingTag"
                                label="Marketing tag"
                                helperText="Examples: 'Offer', 'CO2', 'Hot dish', 'Vegetarian'"
                                fullWidth
                            />
                        </LocalizedStrict>
                        <FormikAllergensField name="productDescription.allergens" />

                        <FormikDeliveryLocationField name="deliveryDetails.location.id" />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-barcode-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Product number" variant="collapsible" expanded={true}>
                        <SelectProductGroupCode />
                        <LocalizedStrict id="product-create-number-field-label" attrs={{ label: true }}>
                            <FormikTextField name="productNumber" label="Internal product number" fullWidth />
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-barcode-field-label" attrs={{ label: true }}>
                            <FormikTextField name="barcode" label="Barcode" fullWidth />
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-availability-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Availability" variant="collapsible" expanded={false}>
                        <AvailabilityDetailsField />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-deadline-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Deadline"
                        variant="switchable"
                        checked={checkedDeadline}
                        onCheckChange={handleChangeDeadline}
                    >
                        <DeadlineField />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-offer-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Offer"
                        variant="button"
                        checked={checkedOffer}
                        expanded={Boolean(values.offerDetails)}
                        onCreateButtonClick={() => setOfferDialogState(true)}
                    >
                        <OfferDetails
                            dialogState={offerDialogState}
                            setDialogState={setOfferDialogState}
                            setCheckedOffer={setCheckedOffer}
                        />
                    </StandardSidePanel>
                </LocalizedStrict>

                <LocalizedStrict id="product-create-item-limits-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Item limits"
                        variant="switchable"
                        checked={checkedItemLimits}
                        onCheckChange={handleChangeItemLimits}
                    >
                        <ItemLimitsField />
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>
        </>
    )
}
